import type { PlanNode } from '@timelog/pm-shared';

/**
 * Flattens the plan tree into the visible row list the virtualizer renders.
 * Plans hold tens of thousands of nodes at unbounded depth (BR-3) — every
 * walk here uses an explicit stack, never recursion.
 */

export type FlatRow = {
  id: string;
  node: PlanNode;
  depth: number;
  parentId: string | null;
  hasChildren: boolean;
  folded: boolean;
  /** Descendants hidden under this row when folded (badge in the fold toggle). */
  hiddenCount: number;
};

export type PlanIndex = {
  /** Visible-row position by node id (selection ranges, keyboard nav). */
  rowIndexById: Map<string, number>;
  nodeById: Map<string, PlanNode>;
  parentById: Map<string, string | null>;
  /** Total descendant count per node (fold badges, drag block sizes). */
  descendantCountById: Map<string, number>;
  totalCount: number;
};

export type FlattenResult = { rows: FlatRow[]; index: PlanIndex };

/** Iterative post-order pass computing every node's descendant count. */
const countDescendants = (plan: PlanNode[]): Map<string, number> => {
  const counts = new Map<string, number>();
  type Frame = { node: PlanNode; childIndex: number };

  for (const root of plan) {
    const stack: Frame[] = [{ node: root, childIndex: 0 }];
    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;
      if (frame.childIndex < frame.node.children.length) {
        stack.push({ node: frame.node.children[frame.childIndex]!, childIndex: 0 });
        frame.childIndex += 1;
      } else {
        stack.pop();
        let total = frame.node.children.length;
        for (const child of frame.node.children) {
          total += counts.get(child.id) ?? 0;
        }
        counts.set(frame.node.id, total);
      }
    }
  }

  return counts;
};

export const flattenPlan = (plan: PlanNode[], foldedIds: ReadonlySet<string>): FlattenResult => {
  const descendantCountById = countDescendants(plan);
  const rows: FlatRow[] = [];
  const rowIndexById = new Map<string, number>();
  const nodeById = new Map<string, PlanNode>();
  const parentById = new Map<string, string | null>();

  let totalCount = 0;

  // Pre-order with an explicit stack; children pushed in reverse so the
  // visit order is reading order (same shape as shared walkPlan).
  type Frame = { node: PlanNode; depth: number; parentId: string | null; hidden: boolean };
  const stack: Frame[] = [];
  for (let index = plan.length - 1; index >= 0; index -= 1) {
    stack.push({ node: plan[index]!, depth: 0, parentId: null, hidden: false });
  }

  while (stack.length > 0) {
    const { node, depth, parentId, hidden } = stack.pop()!;

    totalCount += 1;
    nodeById.set(node.id, node);
    parentById.set(node.id, parentId);

    const folded = foldedIds.has(node.id) && node.children.length > 0;

    if (!hidden) {
      rowIndexById.set(node.id, rows.length);
      rows.push({
        id: node.id,
        node,
        depth,
        parentId,
        hasChildren: node.children.length > 0,
        folded,
        hiddenCount: folded ? (descendantCountById.get(node.id) ?? 0) : 0,
      });
    }

    const childrenHidden = hidden || folded;
    for (let index = node.children.length - 1; index >= 0; index -= 1) {
      stack.push({
        node: node.children[index]!,
        depth: depth + 1,
        parentId: node.id,
        hidden: childrenHidden,
      });
    }
  }

  return {
    rows,
    index: {
      rowIndexById,
      nodeById,
      parentById,
      descendantCountById,
      totalCount,
    },
  };
};
